import { observer } from 'mobx-react-lite'
import React from 'react'
import { Alert, Button, Icon, Scrollbar, SpinnerLoader, Typography } from 'shared/ui'
import { formatPhoneNumberNational } from 'shared/lib'
import { PriceInfo } from 'entities/Billing'
import { useNumbersLayerContext } from 'pages/settings/pages/numbers/context/numbersLayerContext'
import { TotalSecondLine } from 'pages/settings/pages/numbers/pages/ImportTwilioNumbers/ui/TotalSecondLine'
import styles from './styles.module.scss'

export const ImportTwilioNumbers = observer(() => {
  const { importTwilioNumbersStore } = useNumbersLayerContext()

  if (!importTwilioNumbersStore) return null

  const {
    numbers,
    priceInfoStore,
    gracePeriod,
    isEnoughLicenses,
    isLessLicensesThanNumbers,
    numbersLicensesNumber,
    numbersCount,
    handleAddNumber,
    importProcessLoading,
    hasImportNumbersError,
  } = importTwilioNumbersStore

  if (importProcessLoading) return <SpinnerLoader ariaLabel={'ImportTwilioNumbers'} />

  return (
    <div className={styles.content}>
      <Typography
        variant={'body-xl-medium'}
        ellipsis
        color={'var(--content-primary-primary)'}
        ariaLabel={'Numbers'}
      >
        {`${numbers.length} Number${numbers.length > 1 ? 's' : ''}`}
      </Typography>
      {hasImportNumbersError && (
        <Alert
          marginBottom={24}
          item={{
            type: 'error',
            desc: 'We couldn’t import numbers',
          }}
        />
      )}
      <div className={styles.list}>
        <Scrollbar autoHeight autoHeightMax={168}>
          <div className={styles.items}>
            {numbers.map((number) => (
              <div key={number.sid} className={styles.item}>
                <Icon icon={'twilio'} className={styles.icon} />
                <Typography
                  variant={'body-md-regular'}
                  ellipsis
                  color={'var(--content-primary-primary)'}
                  ariaLabel={'Number'}
                >
                  {formatPhoneNumberNational(number.phone_number)}
                </Typography>
              </div>
            ))}
          </div>
        </Scrollbar>
      </div>
      {isLessLicensesThanNumbers && (
        <Alert
          item={{
            type: 'infoBlue',
            title: `Your workspace has ${numbersLicensesNumber} number${
              numbersLicensesNumber > 1 ? 's' : ''
            }  licenses available.`,
            desc: (
              <Typography ariaLabel={'title'} variant={'body-md-regular'}>
                You’ll be charged only for {numbersCount - numbersLicensesNumber} numbers
              </Typography>
            ),
          }}
        />
      )}
      {isEnoughLicenses && (
        <Alert
          item={{
            type: 'infoBlue',
            title: 'You’ll not be charged for these numbers.',
            desc: (
              <Typography ariaLabel={'title'} variant={'body-md-regular'}>
                Your workspace has {numbersLicensesNumber} number licenses available.
              </Typography>
            ),
          }}
        />
      )}
      <PriceInfo
        priceInfoStore={priceInfoStore}
        labelPrefix='Number licenses'
        gracePeriodInfo={{
          totalSecondLine: (
            <TotalSecondLine
              gracePeriod={gracePeriod}
              total={(priceInfoStore.priceInfo?.total_due_today ?? 0) / 100}
            />
          ),
        }}
      />
      <Button
        className={styles.addButton}
        text={`Add ${numbersCount} number${numbersCount > 1 ? 's' : ''}`}
        size='medium'
        onClick={handleAddNumber}
      />
    </div>
  )
})
